import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useGameLoop } from '../Shared/hooks/useGameLoop';
import { useAudio } from '../Shared/hooks/useAudio';
import GameLayout from '../Shared/components/GameLayout/GameLayout';
import Scoreboard from '../Shared/components/Scoreboard/Scoreboard';
import Countdown from '../Shared/components/Countdown/Countdown';
import { GAME_THEME } from '../Shared/constants/GameTheme';

const MomentumSpinner = () => {
    const canvasRef = useRef(null);
    const { beep, ping } = useAudio();

    // Physics Constants
    const G = 1200; // Gravity strength
    const DAMPING = 0.995; // Air resistance/friction

    // Game State Ref
    const stateRef = useRef({
        theta: 0,
        omega: 0,
        running: false,
        startTime: null,
        completed: false,
        hits: 0,
        misses: 0,
        rotations: 0,
        lastTheta: 0,
        flashes: []
    });

    // UI State / Settings
    const [topSize, setTopSize] = useState(25);
    const [bottomSize, setBottomSize] = useState(40);
    const [momentumForce, setMomentumForce] = useState(0.85);
    const [armLength, setArmLength] = useState(150);

    const [currentTime, setCurrentTime] = useState(0);
    const [uiStats, setUiStats] = useState({ hits: 0, misses: 0, rotations: 0 });
    const [finishedRank, setFinishedRank] = useState(null);
    const [highScores, setHighScores] = useState([]);
    const [isCountingDown, setIsCountingDown] = useState(false);
    const [showStartModal, setShowStartModal] = useState(true);
    const [prepMode, setPrepMode] = useState('off');

    const STORAGE_KEY = "momentum_spinner_scores";

    const loadBoard = useCallback(() => {
        const scores = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
        setHighScores(scores);
    }, []);

    const saveScore = (time, hits, misses) => {
        const scores = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
        scores.push({
            time, hits, misses,
            size: `${bottomSize}/${topSize}`,
            date: new Date().toISOString()
        });
        scores.sort((a, b) => a.time - b.time);
        const top = scores.slice(0, 10);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(top));

        const rank = top.findIndex(s => s.time === time && s.hits === hits && s.misses === misses) + 1;
        setFinishedRank(rank > 0 ? rank : "unranked");
        setHighScores(top);
    };

    const resetBoard = () => {
        localStorage.removeItem(STORAGE_KEY);
        setHighScores([]);
    };

    useEffect(() => { loadBoard(); }, [loadBoard]);

    const actualStart = useCallback(() => {
        beep(1000, 0.2);
        const state = stateRef.current;
        state.running = true;
        state.startTime = performance.now();
        state.hits = 0;
        state.misses = 0;
        state.rotations = 0;
        state.omega = 0;
        state.theta = 0;
        state.lastTheta = 0;
        state.completed = false;
        state.flashes = [];
    }, [beep]);

    const reset = useCallback(() => {
        const state = stateRef.current;
        state.running = false;
        state.theta = 0;
        state.omega = 0;
        state.hits = 0;
        state.misses = 0;
        state.rotations = 0;
        state.completed = false;
        state.startTime = null;
        state.flashes = [];
        setCurrentTime(0);
        setUiStats({ hits: 0, misses: 0, rotations: 0 });
        setFinishedRank(null);
    }, []);

    const startGame = useCallback(() => {
        reset();
        setShowStartModal(false);
        setIsCountingDown(true);
    }, [reset]);

    const drawTarget = (ctx, y, z, size, color = GAME_THEME.TARGET_FILL, backColor = GAME_THEME.TARGET_FILL, backStroke = GAME_THEME.TARGET_STROKE) => {
        const targetTheta = stateRef.current.theta + (y > 0 ? 0 : Math.PI);
        const vScale = Math.max(0.05, Math.abs(Math.cos(targetTheta)));
        const isBack = Math.cos(targetTheta) < 0;

        ctx.save();
        ctx.translate(0, y);

        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.ellipse(0, 0, size, size * vScale, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = GAME_THEME.TARGET_STROKE;
        ctx.lineWidth = GAME_THEME.TARGET_STROKE_WIDTH;
        ctx.stroke();

        ctx.restore();
    };

    const loop = useCallback((now, dt) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const state = stateRef.current;

        const cx = canvas.width / 2;
        const cy = canvas.height / 2;

        if (state.running && !state.completed) {
            const torque = -G * (bottomSize / 40) * Math.sin(state.theta);
            const alpha = torque / 1000;

            state.omega += alpha * dt;
            state.omega *= DAMPING;
            state.theta += state.omega * dt;

            if (Math.abs(state.theta) > Math.PI * 2) {
                state.completed = true;
                const finalTime = (performance.now() - state.startTime) / 1000;
                setCurrentTime(finalTime);
                saveScore(finalTime, state.hits, state.misses);
            }
            state.lastTheta = state.theta;

            if (state.startTime) {
                setCurrentTime((performance.now() - state.startTime) / 1000);
            }

            setUiStats({
                hits: state.hits,
                misses: state.misses,
                rotations: Math.floor(Math.abs(state.theta) / (Math.PI * 2))
            });
        }

        // Update Flashes
        state.flashes = state.flashes.filter(f => f.life > 0);
        state.flashes.forEach(f => {
            f.life -= dt;
            f.y -= dt * 60;
        });

        // Draw
        // ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = GAME_THEME.BACKGROUND_COLOR;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // --- MAIN FRONT VIEW ---
        const mainCx = cx - 100;
        ctx.save();
        ctx.translate(mainCx, cy);

        const cosTheta = Math.cos(state.theta);
        const sinTheta = Math.sin(state.theta);

        const by = armLength * cosTheta;
        const bz = armLength * sinTheta;
        const ty = -armLength * cosTheta;
        const tz = -armLength * sinTheta;

        const targetsFront = [
            { y: by, z: bz, size: bottomSize, color: GAME_THEME.TARGET_FILL },
            { y: ty, z: tz, size: topSize, color: GAME_THEME.TARGET_FILL }
        ];
        targetsFront.sort((a, b) => b.z - a.z);

        drawTarget(ctx, targetsFront[0].y, targetsFront[0].z, targetsFront[0].size, targetsFront[0].color, targetsFront[0].backColor, targetsFront[0].backStroke);

        ctx.strokeStyle = '#555';
        ctx.lineWidth = 10;
        ctx.beginPath();
        ctx.moveTo(0, ty);
        ctx.lineTo(0, by);
        ctx.stroke();

        ctx.fillStyle = '#888';
        ctx.beginPath();
        ctx.arc(0, 0, 8, 0, Math.PI * 2);
        ctx.fill();

        drawTarget(ctx, targetsFront[1].y, targetsFront[1].z, targetsFront[1].size, targetsFront[1].color, targetsFront[1].backColor, targetsFront[1].backStroke);

        ctx.restore();
        ctx.save();
        ctx.translate(mainCx, cy);
        for (const f of state.flashes) {
            ctx.fillStyle = `rgba(46, 204, 113, ${f.life})`;
            ctx.font = `bold 24px Inter, sans-serif`;
            ctx.textAlign = "center";
            ctx.fillText("HIT!", f.x, f.y);
        }
        ctx.restore();

        // --- SIDE VIEW ---
        const sideCx = cx + 250;
        ctx.save();
        ctx.translate(sideCx, cy);

        ctx.strokeStyle = 'rgba(255,255,255,0.1)';
        ctx.strokeRect(-100, -armLength - 50, 200, (armLength + 50) * 2);
        ctx.fillStyle = 'rgba(255,255,255,0.2)';
        ctx.fillText("SIDE VIEW", -30, -armLength - 30);

        ctx.strokeStyle = '#666';
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.moveTo(tz * 0.5, ty);
        ctx.lineTo(bz * 0.5, by);
        ctx.stroke();

        ctx.save();
        ctx.translate(bz * 0.5, by);
        ctx.rotate(-state.theta + Math.PI / 2);
        ctx.fillStyle = GAME_THEME.TARGET_FILL;
        ctx.fillRect(-bottomSize, -4, bottomSize * 2, 8);
        ctx.strokeStyle = GAME_THEME.TARGET_STROKE;
        ctx.lineWidth = GAME_THEME.TARGET_STROKE_WIDTH;
        ctx.strokeRect(-bottomSize, -4, bottomSize * 2, 8);
        ctx.restore();

        ctx.save();
        ctx.translate(tz * 0.5, ty);
        ctx.rotate(-state.theta + Math.PI / 2);
        ctx.fillStyle = GAME_THEME.TARGET_FILL;
        ctx.fillRect(-topSize, -4, topSize * 2, 8);
        ctx.strokeStyle = GAME_THEME.TARGET_STROKE;
        ctx.lineWidth = GAME_THEME.TARGET_STROKE_WIDTH;
        ctx.strokeRect(-topSize, -4, topSize * 2, 8);
        ctx.restore();

        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.arc(0, 0, 4, 0, Math.PI * 2);
        ctx.fill();

        ctx.restore();
    }, [armLength, topSize, bottomSize]);

    useGameLoop(loop, true);

    const handleCanvasClick = (e) => {
        const state = stateRef.current;
        if (!state.running || state.completed) return;

        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const mxCenter = e.clientX - rect.left - canvas.width / 2;
        const mx = mxCenter + 100;
        const my = e.clientY - rect.top - canvas.height / 2;

        const cosTheta = Math.cos(state.theta);
        const vScaleHit = Math.max(0.15, Math.abs(cosTheta));
        const effectiveForce = momentumForce * Math.abs(cosTheta);

        // Check Top Target
        const ty = -armLength * cosTheta;
        if (Math.pow(mx / topSize, 2) + Math.pow((my - ty) / (topSize * vScaleHit), 2) <= 1.2) {
            state.hits++;
            state.omega += (ty / armLength) * effectiveForce;
            ping();
            state.flashes.push({ x: mx, y: my, life: 1.0 });
            return;
        }

        // Check Bottom Target
        const by = armLength * cosTheta;
        if (Math.pow(mx / bottomSize, 2) + Math.pow((my - by) / (bottomSize * vScaleHit), 2) <= 1.2) {
            state.hits++;
            state.omega += (by / armLength) * effectiveForce;
            ping();
            state.flashes.push({ x: mx, y: my, life: 1.0 });
            return;
        }

        state.misses++;
    };

    const sidebarContent = (
        <>
            <h3 style={{ marginTop: 0 }}>Momentum Spinner</h3>
            <p style={{ fontSize: '12px', color: '#aaa' }}>
                Face-on spinner. Click the targets as they swing towards/away from you to build momentum.
            </p>

            <label>Bottom Target Size: {bottomSize}px</label>
            <input type="range" min="20" max="60" value={bottomSize} onChange={e => setBottomSize(Number(e.target.value))} />

            <label>Top Target Size: {topSize}px</label>
            <input type="range" min="20" max="60" value={topSize} onChange={e => setTopSize(Number(e.target.value))} />

            <label>Momentum Force: {momentumForce.toFixed(1)}</label>
            <input type="range" min="0.1" max="3" step="0.1" value={momentumForce} onChange={e => setMomentumForce(Number(e.target.value))} />

            <label>Arm Length: {armLength}px</label>
            <input type="range" min="100" max="250" value={armLength} onChange={e => setArmLength(Number(e.target.value))} />

            <button
                onClick={startGame}
                style={{
                    background: GAME_THEME.BUTTON_BG,
                    color: 'white',
                    border: '1px solid #444',
                    padding: '10px',
                    borderRadius: '4px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    width: '100%',
                    marginBottom: '15px'
                }}
            >
                Start Game
            </button>

            <Scoreboard
                stats={{
                    "Time": currentTime.toFixed(2) + "s",
                    "Clicks": uiStats.hits,
                    "Speed": Math.abs(stateRef.current.omega).toFixed(1)
                }}
                highScores={highScores}
                onReset={resetBoard}
                title="Fastest Times (3 Rotations)"
            />

            <label>Prep Mode</label>
            <select value={prepMode} onChange={(e) => setPrepMode(e.target.value)}>
                <option value="off">Off</option>
                <option value="on">On (Delay)</option>
            </select>
        </>
    );

    const handleCountdownFinish = useCallback(() => {
        setIsCountingDown(false);
        actualStart();
    }, [actualStart]);

    return (
        <GameLayout
            sidebarContent={sidebarContent}
            canvasRef={canvasRef}
            onCanvasClick={handleCanvasClick}
            gameRunning={stateRef.current.running || isCountingDown}
            onQuit={() => {
                reset();
                setIsCountingDown(false);
                setShowStartModal(true);
            }}
        >
            {isCountingDown && (
                <Countdown prepMode={prepMode === 'on'} onFinish={handleCountdownFinish} />
            )}

            {showStartModal && (
                <div style={{
                    position: 'absolute',
                    top: 0, left: 0, width: '100%', height: '100%',
                    background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10
                }}>
                    <div style={{
                        background: '#222', padding: '50px', borderRadius: '16px', textAlign: 'center',
                        border: '1px solid #444', color: '#fff', boxShadow: '0 15px 35px rgba(0,0,0,0.6)', minWidth: '350px'
                    }}>
                        <h1 style={{ marginTop: 0, color: '#9b59b6', fontSize: '2.5em', marginBottom: '10px' }}>Momentum Spinner</h1>
                        <p style={{ color: '#ccc', marginBottom: '30px', fontSize: '1.1em' }}>
                            Rotate the device 360 degrees.<br />
                            Timing is key as targets turn edge-on!
                        </p>
                        <button
                            onClick={startGame}
                            style={{
                                padding: '16px 40px', fontSize: '20px', fontWeight: 'bold',
                                background: GAME_THEME.BUTTON_BG, color: 'white', border: '1px solid #444', borderRadius: '8px', cursor: 'pointer',
                                transition: 'all 0.2s', margin: '0 auto'
                            }}
                        >
                            START GAME
                        </button>
                    </div>
                </div>
            )}

            {finishedRank !== null && (
                <div style={{
                    position: 'absolute',
                    top: 0, left: 0, width: '100%', height: '100%',
                    background: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10
                }}>
                    <div style={{
                        background: '#222', padding: '40px', borderRadius: '12px', textAlign: 'center',
                        border: '1px solid #444', color: '#fff', boxShadow: '0 10px 25px rgba(0,0,0,0.5)', minWidth: '300px'
                    }}>
                        <h2 style={{ marginTop: 0, color: '#f1c40f', fontSize: '2em' }}>Rotation Complete!</h2>
                        <div style={{ margin: '20px 0', fontSize: '1.2em' }}>
                            <div style={{ fontSize: '2.5em', fontWeight: 'bold' }}>{currentTime.toFixed(2)}s</div>
                            <div style={{ color: '#aaa' }}>{uiStats.hits} Clicks registered</div>
                        </div>
                        <div style={{ background: '#333', padding: '10px', borderRadius: '5px', marginBottom: '25px' }}>
                            {finishedRank === "unranked" ? (
                                <span style={{ color: '#aaa' }}>Better luck next time! (Not in top 10)</span>
                            ) : (
                                <>Ranked <strong style={{ color: '#2ecc71' }}>#{finishedRank}</strong> locally!</>
                            )}
                        </div>
                        <button onClick={startGame} style={{
                            padding: '12px 30px', fontSize: '18px', fontWeight: 'bold',
                            background: '#9b59b6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', margin: '0 auto'
                        }}>
                            Go Again
                        </button>
                    </div>
                </div>
            )}
        </GameLayout>
    );
};

export default MomentumSpinner;
